import * as React from 'react';
import FormGroup, { IInstruction } from '@ui-library/formGroup';
import flat from 'flat';
import isString from 'lodash/isString';
import { isValidJson } from '@components/common/util/util';
import { FixMe } from '@utils/types';
import { MetaData } from './dynamicSelect';

export interface Group {
  title: string;
  fields: Field[];
  instruction?: IInstruction;
  sectionNote?: string;
}

export type FormConfig = Record<string, unknown>;
export type FormSecrets = Record<string, unknown>;

export type Schema = Group[];
interface IDestinationSettingsProps {
  schema: Schema;
  onChange: (settings: FormConfig, isValid: boolean) => void;
  initialSettings?: FormConfig;
  transformFields?: (fields: Field[]) => Field[];
  secretConfig?: FormSecrets;
  disabled?: boolean;
  validate?: boolean;
  metadata?: MetaData;
}

interface PreRequisiteField {
  name: string;
  selectedValue: string;
}

export interface Field {
  subType: string;
  value: string;
  preRequisiteField: PreRequisiteField[] | PreRequisiteField;
  required: boolean;
  default?: Record<string, unknown>;
  obtainValueFromField?: { name: string; key: string };
}

interface IState {
  settings: FormConfig;
}

// TODO - Move it to common
export default class FormSchema extends React.Component<IDestinationSettingsProps, IState> {
  static validate = (settings: FormConfig, fields: Field[]) => {
    const req = Object.entries(settings).map(function val([k, value]: [string, FixMe]): boolean {
      const v = isString(value) ? value.trim() : value;
      if (v) {
        if (typeof v === 'object' && !Array.isArray(v)) {
          return Object.entries(flat({ [k]: v }))
            .map(val)
            .every(Boolean);
        }
        if (
          fields.some(
            (field: Field) => Object.values(field).includes(k) && field.subType === 'JSON',
          )
        ) {
          return isValidJson(v);
        }
      } else if (!v) {
        // corresponding fields is an array, when there are multiple fields with same value
        const correspondingFields = fields.filter(
          (elem: Field) => elem.value === k || k.endsWith(elem.value),
        );
        // if field has a preRequisiteField and its not active, do not do required validation
        return correspondingFields.every((field) => {
          const preReqField = field?.preRequisiteField;
          if (preReqField) {
            if (Array.isArray(preReqField)) {
              if (
                preReqField.some(
                  (elem) =>
                    Object.prototype.hasOwnProperty.call(elem, 'selectedValue') &&
                    settings[elem.name] !== elem.selectedValue,
                )
              ) {
                return true;
              }
            } else if (
              Object.prototype.hasOwnProperty.call(preReqField, 'selectedValue') &&
              settings[preReqField.name] !== preReqField.selectedValue
            ) {
              return true;
            }
          }
          return !(field && field.required);
        });
      }
      return true;
    });
    return req.every(Boolean);
  };

  constructor(props: IDestinationSettingsProps) {
    super(props);
    this.state = {
      settings: {},
    };
  }

  public onSettingsChange = (settings: FormConfig, isError: boolean) => {
    const { onChange, schema, validate = true } = this.props;
    let isValid = false;
    if (validate) {
      const fields = schema.reduce((acc: Field[], group: Group) => acc.concat(group.fields), []);
      isValid = FormSchema.validate(settings, fields);
    }
    onChange(settings, isError || isValid);
  };

  public onChange = (settings: FormConfig, isError: boolean) => {
    this.setState(
      (prevState: IState) => ({
        settings: {
          ...prevState.settings,
          ...settings,
        },
      }),
      () => this.onSettingsChange(this.state.settings, isError),
    );
  };

  private getFields(fields: Field[]) {
    const { transformFields } = this.props;
    if (transformFields) {
      return transformFields(fields);
    }
    return fields;
  }

  public render() {
    const { initialSettings, disabled, schema, secretConfig, metadata } = this.props;
    return (
      <div>
        {schema.map((group: Group) => (
          <FormGroup
            metadata={metadata}
            secretConfig={secretConfig}
            key={group.title}
            title={group.title}
            instruction={group.instruction}
            fields={this.getFields(group.fields)}
            sectionNote={group.sectionNote}
            onStateChange={this.onChange}
            initialSettings={initialSettings}
            disabled={!!disabled}
          />
        ))}
      </div>
    );
  }
}
